import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useTranslation } from "../../hooks";
import { usePlaybackContext } from "../Playback";
import { SubtitleContext } from "./SubtitleContext";
import subtitleReducer, { SUBTITLE_REDUCER_INITIAL_STATE } from "./subtitleReducer";
import { fetchAndParseSrtSubtitles, getSubtitleOptions } from "./utils";
import { jsx as _jsx } from "react/jsx-runtime";
var SubtitleProvider = function SubtitleProvider(_ref) {
  var children = _ref.children,
    videoSubtitles = _ref.videoSubtitles,
    onSubtitlesChange = _ref.onSubtitlesChange,
    availableSubtitleLanguages = _ref.availableSubtitleLanguages,
    defaultSubtitleLang = _ref.defaultSubtitleLang;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _usePlaybackContext = usePlaybackContext(),
    videoRef = _usePlaybackContext.videoRef,
    isReady = _usePlaybackContext.isReady;
  var _useReducer = useReducer(subtitleReducer, SUBTITLE_REDUCER_INITIAL_STATE),
    _useReducer2 = _slicedToArray(_useReducer, 2),
    _useReducer2$ = _useReducer2[0],
    subtitleOptions = _useReducer2$.subtitleOptions,
    subtitleOptionsType = _useReducer2$.subtitleOptionsType,
    currentSubtitleLang = _useReducer2$.currentSubtitleLang,
    currentExternalSubtitles = _useReducer2$.currentExternalSubtitles,
    dispatch = _useReducer2[1];
  var currentSubtitleLangValue = currentSubtitleLang === null || currentSubtitleLang === void 0 ? void 0 : currentSubtitleLang.value;

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  var validateActiveTextTrack = useCallback(function () {
    var textTracks = videoRef.current ? Array.from(videoRef.current.textTracks) : [];
    if (!textTracks.length) {
      return;
    }
    textTracks.forEach(function (track) {
      // Use track's mode values according to: https://developer.mozilla.org/en-US/docs/Web/API/TextTrack/mode
      if (currentSubtitleLangValue === 'none' || availableSubtitleLanguages && !availableSubtitleLanguages.includes(String(currentSubtitleLangValue))) {
        track.mode = 'disabled';
      } else {
        track.mode = currentSubtitleLangValue === track.language ? 'showing' : 'hidden';
      }
    });
    // Ignoring the availableSubtitleLanguages array because we're including the stringified version.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef, currentSubtitleLangValue,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  JSON.stringify(availableSubtitleLanguages)]);
  var currentTextTracks = videoRef.current ? Array.from(videoRef.current.textTracks) : [];

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(function () {
    if (!isReady) {
      return;
    }
    // Validate and setup subtitle options for this video
    var _getSubtitleOptions = getSubtitleOptions({
        currentTextTracks: currentTextTracks,
        t: t,
        videoSubtitles: videoSubtitles,
        availableSubtitleLanguages: availableSubtitleLanguages,
        defaultSubtitleLang: defaultSubtitleLang,
        currentSubtitleLang: currentSubtitleLang
      }),
      options = _getSubtitleOptions.subtitleOptions,
      optionsType = _getSubtitleOptions.subtitleOptionsType,
      activeSubtitleOption = _getSubtitleOptions.activeSubtitleOption;
    dispatch({
      type: 'SETUP_SUBTITLE_OPTIONS',
      subtitleOptions: options,
      subtitleOptionsType: optionsType,
      currentSubtitleLang: activeSubtitleOption
    });
    // Ignoring currentSubtitleLang, defaultSubtitleLang and @crehana/i18n t function.
    // Ignoring the availableSubtitleLanguages and videoSubtitles because we're including the stringified versions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, videoRef,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  JSON.stringify(currentTextTracks),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  JSON.stringify(availableSubtitleLanguages),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  JSON.stringify(videoSubtitles)]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(function () {
    // Validate and include external vtt/webvtt subtitle tracks if available
    if (isReady && subtitleOptionsType === 'externalSupported' && subtitleOptions !== null && subtitleOptions !== void 0 && subtitleOptions.length) {
      subtitleOptions.forEach(function (sub) {
        if (videoRef.current && sub.src) {
          var track = document.createElement('track');
          track.kind = 'subtitles';
          track.label = sub.label;
          track.srclang = sub.value;
          track.src = sub.src;
          track.addEventListener('load', validateActiveTextTrack);
          videoRef.current.appendChild(track);
        }
      });
    }
    // Ignoring the subtitleOptions array because we're including the stringified version.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  JSON.stringify(subtitleOptions), subtitleOptionsType, validateActiveTextTrack, videoRef]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(function () {
    if (!isReady) {
      return;
    }
    if (subtitleOptionsType === 'hlsTextTracks' || subtitleOptionsType === 'externalSupported') {
      // Subtitle tracks are already included in the video element
      validateActiveTextTrack();
    } else if (subtitleOptions && subtitleOptionsType === 'externalSrt') {
      // External srt subtitles only if no other supported track is found for current video.
      // These need to be parsed and included as a custom component
      var activeSubtitleOption = currentSubtitleLangValue ? subtitleOptions.find(function (sub) {
        return sub.value === currentSubtitleLangValue;
      }) : undefined;
      if (activeSubtitleOption !== null && activeSubtitleOption !== void 0 && activeSubtitleOption.src) {
        fetchAndParseSrtSubtitles(activeSubtitleOption.src).then(function (parsedSubtitles) {
          dispatch({
            type: 'CHANGE_CURRENT_EXTERNAL_SUBTITLES',
            currentExternalSubtitles: parsedSubtitles,
            t: t
          });
        })["catch"](function () {
          dispatch({
            type: 'CHANGE_CURRENT_EXTERNAL_SUBTITLES',
            currentExternalSubtitles: undefined,
            t: t
          });
        });
      } else {
        dispatch({
          type: 'CHANGE_CURRENT_EXTERNAL_SUBTITLES',
          currentExternalSubtitles: undefined,
          t: t
        });
      }
    }
    // Ignoring @crehana/i18n t function.
    // Ignoring the subtitleOptions array because we're including the stringified version.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  JSON.stringify(subtitleOptions), subtitleOptionsType, currentSubtitleLangValue, validateActiveTextTrack]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  var changeSubtitleLang = useCallback(function (subtitleLang) {
    dispatch({
      type: 'CHANGE_CURRENT_SUBTITLE_LANG',
      currentSubtitleLang: subtitleLang
    });
    if (onSubtitlesChange) {
      onSubtitlesChange(subtitleLang.value.toString());
    }
    // Ignoring onSubtitlesChange, assuming it doesn't change after the first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var hasVisibleTextTrack = !!currentTextTracks.find(function (track) {
    return track.mode === 'showing';
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  var contextValue = useMemo(function () {
    return {
      subtitleOptions: subtitleOptions,
      currentSubtitleLang: currentSubtitleLang,
      currentExternalSubtitles: currentExternalSubtitles,
      changeSubtitleLang: changeSubtitleLang,
      hasVisibleTextTrack: hasVisibleTextTrack
    };
  },
  // Ignoring the subtitleOptions and currentExternalSubtitles because we're including the stringified versions.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
  // eslint-disable-next-line react-hooks/exhaustive-deps
  JSON.stringify(subtitleOptions), currentSubtitleLangValue,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  JSON.stringify(currentExternalSubtitles), changeSubtitleLang, hasVisibleTextTrack]);
  return /*#__PURE__*/_jsx(SubtitleContext.Provider, {
    value: contextValue,
    children: children
  });
};
export default SubtitleProvider;